import * as React from "react";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Box, Button, FormControlLabel, Switch, Modal, Divider, Tab } from "@mui/material";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import { useAtom } from "jotai";
import { scannerRequestStateAtom, advancedSettingsAtom } from "../../../components/Atoms/atoms";
import { listOfWeights } from "../utils/common";
import AdvancedQuestionInputs from "./components/AdvancedQuestionInputs";
import CustomTooltip from "../components/CustomTooltip";
import MRScor from "./MRScor";
import HelpIcon from "@mui/icons-material/Help";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
const style = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	width: 1200,
	height: "90dvh",
	bgcolor: "#F5F5F5",
	border: "2px solid #F5F5F5",
	boxShadow: 24,
	p: 2,
};

export default function AdvancedSettingsModal(props) {
	const {
		handleMCExamChange,
		navigate,
		token,
		ccid,
		classNumList,
		handlePrint,
		instructorClasses,
		confirmedOfficeInstructor,
		handleDoneOfficeInstructor,
		ccidString,
	} = props;
	const [open, setOpen] = useState(false);
	const [tabValue, setTabValue] = React.useState("1");

	const [activeTool, setActiveTool] = useState(""); // "weight" | "omit" | "mr"

	const [activeWeight, setActiveWeight] = useState(null);
	const [state, setState] = useAtom(scannerRequestStateAtom);
	const [advancedSettings, setAdvancedSettings] = useAtom(advancedSettingsAtom);
	const [draft, setDraft] = useState({});

	const isDraggingRef = useRef(false);
	const dragActionRef = useRef("add"); // "add" | "remove"
	const dragKeyIndexRef = useRef(null);
	const workingSetRef = useRef(null); // Set<number> while dragging
	const rafIdRef = useRef(null);

	const [canSubmit, setCanSubmit] = useState(true);

	useEffect(() => {
		if (
			state.hqNum &&
			state.lqNum &&
			Number(state.hqNum) &&
			Number(state.lqNum) &&
			advancedSettings.mrscor &&
			state.keys
		) {
			// calculate total max score on exam
			// first get number of questions, which is (hq - lq + 1) minus any omitted questions (use state.keys[0] and not draft)
			const lq = Number(state.lqNum);
			const hq = Number(state.hqNum);
			let numQuestions = hq - lq + 1;
			const items = state.keys[0]?.itemsByQuestion || {};
			Object.values(items).forEach((attrs) => {
				if (attrs && attrs.omit) numQuestions -= 1;
			});

			// take 1/3 of numquestions
			const oneThird = Math.ceil(numQuestions / 3);
			// convert to string and zfill 3, then split into 3 values
			const totalStr = String(oneThird).padStart(3, "0");
			const n = totalStr[0];
			const o = totalStr[1];
			const p = totalStr[2];
			setState((prev) => ({ ...prev, n, o, p }));
		} else if (!advancedSettings.mrscor) {
			setState((prev) => ({ ...prev, n: "1", o: "0", p: "0" }));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.keys, advancedSettings.mrscor]);

	function ensureKey(prevState, keyIndex) {
		const versionsArr = getVersionKeysArray(prevState);
		const entry = versionsArr[keyIndex];

		// If we don't have this key yet (e.g. user just bumped nKeys up), seed a blank one now.
		// We'll create a new versionKey if needed. We'll default to (keyIndex+1).toString().
		const versionKey = entry ? entry.versionKey : String(keyIndex + 1);

		const keysCopy = { ...(prevState.keys || {}) };
		const existingRaw = keysCopy[versionKey] || {};

		// Normalize into a consistent shape for the rest of the component:
		const normalizedExisting = {
			...existingRaw,
			version: existingRaw.version || versionKey,
			itemsByQuestion: existingRaw.itemsByQuestion || {}, // <- THIS is from buildKeysStateFromApi
		};

		return {
			versionKey,
			keysCopy,
			existing: normalizedExisting,
		};
	}

	function keyDataAt(currentState, idx) {
		const versionsArr = getVersionKeysArray(currentState);
		const entry = versionsArr[idx];
		if (!entry) return {};
		return entry.data || {};
	}

	function getVersionKeysArray(state) {
		// Preserve insertion order so tab indexes stay attached to the same key
		// while the user edits version labels.
		if (!state.keys || typeof state.keys !== "object") return [];

		// Turn { "1": {...}, "2": {...} } into [{versionKey:"1", data:{...}}, ...]
		const entries = Object.entries(state.keys).map(([versionKey, data]) => {
			return {
				versionKey,
				versionLabel: data.version || versionKey, // this is what you show as "Key X"
				data,
			};
		});

		return entries;
	}

	const getKeysWithVersion = useCallback(() => {
		const keys = [];
		for (let i = 0; i < Number(state.nKeys || 0); i++) {
			const version = keyDataAt(state, i).version;
			if (version && version.trim().length > 0) keys.push(version.trim());
		}
		return keys;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state]);

	useEffect(() => {
		if (
			(advancedSettings.compositeScoring || advancedSettings.mrscor) &&
			state.nKeys > 1 &&
			getKeysWithVersion().length !== Number(state.nKeys)
		) {
			setCanSubmit(false);
		} else {
			setCanSubmit(true);
		}
	}, [advancedSettings, state.nKeys, getKeysWithVersion]);

	useEffect(() => {
		if (!state.nKeys) return;
		setState((prev) => {
			let next = { ...prev };
			for (let i = 0; i < Number(prev.nKeys); i++) {
				next = seedItemsForRange(next, i);
			}
			return next;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.lqNum, state.hqNum, state.nKeys]);

	useEffect(() => {
		const onUp = () => endDrag();
		const onLeave = () => endDrag();

		window.addEventListener("mouseup", onUp);
		window.addEventListener("touchend", onUp, { passive: true });
		window.addEventListener("mouseleave", onLeave);

		return () => {
			window.removeEventListener("mouseup", onUp);
			window.removeEventListener("touchend", onUp);
			window.removeEventListener("mouseleave", onLeave);
		};
	}, []);

	// Start a drag on a specific key/tab
	const beginDrag = (q, keyIndex) => {
		isDraggingRef.current = true;
		dragKeyIndexRef.current = keyIndex;

		// snapshot the current draft set for this key into a working Set
		const cur = getDraftForKey(keyIndex);
		workingSetRef.current = new Set(cur);

		// decide action based on whether q is already selected
		dragActionRef.current = workingSetRef.current.has(q) ? "remove" : "add";

		// apply the action to the first cell
		if (dragActionRef.current === "add") workingSetRef.current.add(q);
		else workingSetRef.current.delete(q);

		// schedule a render
		scheduleDraftRender();
	};

	const hoverDrag = (q) => {
		if (!isDraggingRef.current || workingSetRef.current == null) return;
		if (dragActionRef.current === "add") workingSetRef.current.add(q);
		else workingSetRef.current.delete(q);
		scheduleDraftRender();
	};

	const endDrag = () => {
		if (!isDraggingRef.current) return;
		isDraggingRef.current = false;

		// commit the working set into the real draft for the active key
		const keyIndex = dragKeyIndexRef.current;
		const finalSet = new Set(workingSetRef.current || []);
		setDraft((prev) => ({ ...prev, [keyIndex]: finalSet }));

		// cleanup
		dragKeyIndexRef.current = null;
		workingSetRef.current = null;
		if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
		rafIdRef.current = null;
	};

	// rAF to avoid re-render storms
	const scheduleDraftRender = () => {
		if (rafIdRef.current) return;
		rafIdRef.current = requestAnimationFrame(() => {
			rafIdRef.current = null;
			const keyIndex = dragKeyIndexRef.current;
			if (keyIndex == null) return;
			// Write the working set into state (causes one render per frame)
			const snapshot = new Set(workingSetRef.current || []);
			setDraft((prev) => ({ ...prev, [keyIndex]: snapshot }));
		});
	};

	const handleChangeTabs = (event, newValue) => {
		setTabValue(newValue);
	};

	const handleOpen = () => setOpen(true);

	const handleClose = () => setOpen(false);

	// Initialize missing items based on lq/hq
	const seedItemsForRange = (prev, keyIndex) => {
		const lq = Number(prev.lqNum);
		const hq = Number(prev.hqNum);
		if (!Number.isFinite(lq) || !Number.isFinite(hq) || hq < lq) return prev;

		const { versionKey, keysCopy, existing } = ensureKey(prev, keyIndex);
		const items = { ...(existing.itemsByQuestion || {}) };

		// Remove questions outside range
		for (const qStr of Object.keys(items)) {
			const qNum = Number(qStr);
			if (qNum < lq || qNum > hq) {
				delete items[qNum];
			}
		}

		// Add missing questions inside range
		for (let q = lq; q <= hq; q++) {
			if (!items[q]) {
				items[q] = { weight: undefined, omit: false, mr: false };
			}
		}

		// Rebuild weight buckets if you still want to keep that around
		const weights = deriveWeightsBuckets(items);

		keysCopy[versionKey] = {
			...existing,
			itemsByQuestion: items,
			weights,
		};

		return { ...prev, keys: keysCopy };
	};
	const deriveWeightsBuckets = (items) => {
		const buckets = {};
		Object.entries(items || {}).forEach(([qStr, attrs]) => {
			const q = Number(qStr);
			if (!attrs) return;
			// Only bucket questions that are *not omitted* and have a numeric weight
			if (!attrs.omit && attrs.weight != null && String(attrs.weight).length) {
				const w = String(attrs.weight);
				if (!buckets[w]) buckets[w] = [];
				buckets[w].push(q);
			}
		});
		// Sort for stability
		Object.keys(buckets).forEach((w) => buckets[w].sort((a, b) => a - b));
		return buckets;
	};

	const toggleSelectQuestion = (q, keyIndex) => {
		setDraftForKey(keyIndex, (set) => {
			const next = new Set(set);
			if (next.has(q)) next.delete(q);
			else next.add(q);
			return next;
		});
	};

	const commitDraftForKey = (keyIndex) => {
		const selected = Array.from(getDraftForKey(keyIndex));
		if (selected.length === 0) return;

		if (activeTool === "weight" && !activeWeight) {
			alert("Choose a weight first.");
			return;
		}

		setState((prev) => {
			const { versionKey, keysCopy, existing } = ensureKey(prev, keyIndex);
			const items = { ...(existing.itemsByQuestion || {}) };

			selected.forEach((q) => {
				items[q] = {
					weight: items[q]?.weight,
					omit: !!items[q]?.omit,
					mr: !!items[q]?.mr,
				};
				if (activeTool === "weight") {
					items[q].weight = String(activeWeight);
				} else if (activeTool === "omit") {
					items[q].omit = !items[q].omit;
				} else if (activeTool === "mr") {
					items[q].mr = !items[q].mr;
				}
			});

			const weights = deriveWeightsBuckets(items);

			keysCopy[versionKey] = {
				...existing,
				itemsByQuestion: items,
				weights,
			};

			return { ...prev, keys: keysCopy };
		});

		clearDraft(keyIndex);
	};

	const resetKeyItems = (keyIndex) => {
		setState((prev) => {
			const { versionKey, keysCopy, existing } = ensureKey(prev, keyIndex);
			const items = { ...(existing.itemsByQuestion || {}) };

			Object.keys(items).forEach((q) => {
				items[q] = { weight: undefined, omit: false, mr: false };
			});

			const weights = deriveWeightsBuckets(items);

			keysCopy[versionKey] = {
				...existing,
				itemsByQuestion: items,
				weights,
			};

			return { ...prev, keys: keysCopy };
		});
	};

	const getDraftForKey = (keyIndex) => draft[keyIndex] ?? new Set();

	const setDraftForKey = (keyIndex, updater) => {
		setDraft((prev) => {
			const cur = new Set(prev[keyIndex] ?? []);
			const next = updater(cur);
			return { ...prev, [keyIndex]: next };
		});
	};

	const clearDraft = (keyIndex) => setDraftForKey(keyIndex, () => new Set());

	const columns = 10;

	const numQuestions =
		state.lqNum && state.hqNum ? Number(state.hqNum) - Number(state.lqNum) + 1 : 0;
	const numRows = Math.ceil(numQuestions / columns);

	const toggleSelectRow = (rowIndex, keyIndex) => {
		const lq = Number(state.lqNum);
		if (!lq) return;
		const startQ = lq + rowIndex * columns;
		const endQ = Math.min(startQ + columns - 1, state.hqNum);
		for (let q = startQ; q <= endQ; q++) {
			toggleSelectQuestion(q, keyIndex);
		}
	};

	const getTotalsForKey = (keyIndex) => {
		const { existing } = ensureKey(state, keyIndex);
		const items = existing.itemsByQuestion || {};

		let total = 0;
		let weighted = 0;
		let mrCount = 0;
		let totalMarks = 0;

		const lq = Number(state.lqNum);
		const hq = Number(state.hqNum);
		const hasRange = Number.isFinite(lq) && Number.isFinite(hq) && hq >= lq;

		if (Object.keys(items).length === 0 && hasRange) {
			total = hq - lq + 1;
			weighted = 0;
			mrCount = 0;
			totalMarks = total;
			return { total, weighted, mrCount, totalMarks };
		}

		Object.values(items).forEach((attrs) => {
			total += 1;
			const notOmitted = attrs && !attrs.omit;
			const hasWeight = attrs && attrs.weight != null && String(attrs.weight).trim().length > 0;
			const isMR = attrs && attrs.mr;

			if (hasWeight && notOmitted) weighted += 1;
			if (isMR && notOmitted) mrCount += 1;

			if (notOmitted) {
				const wNum = Number(attrs?.weight);
				const contrib = Number.isFinite(wNum) && wNum > 0 ? wNum : 1;
				totalMarks += contrib;
			}
		});

		return { total, weighted, mrCount, totalMarks };
	};

	const keySummaries = useMemo(() => {
		const n = Number(state.nKeys || 0);
		return Array.from({ length: n }).map((_, idx) => {
			const { total, weighted, mrCount, totalMarks } = getTotalsForKey(idx);
			const label = keyDataAt(state, idx).version || String(idx + 1);

			return { idx, label, total, weighted, mrCount, totalMarks };
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.nKeys, state.lqNum, state.hqNum, state.keys]);

	const versionSummary = React.useMemo(() => {
		const numberOfKeys = Number(state.nKeys || 0);
		const versions = getKeysWithVersion();
		if (versions.length === 0) return ["No version codes entered.", "red"];
		if (versions.length < numberOfKeys) {
			return [`Version codes entered for ${versions.length} of ${numberOfKeys} keys.`, "yellow"];
		}
		return [`Version codes entered for all keys.`, "green"];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.nKeys, state.keys]);

	const inRangeQs = () => {
		const lq = Number(state.lqNum);
		const hq = Number(state.hqNum);
		if (!Number.isFinite(lq) || !Number.isFinite(hq) || hq < lq) return [];
		const arr = [];
		for (let q = lq; q <= hq; q++) arr.push(q);
		return arr;
	};

	const areAllSelectedInRange = (keyIndex) => {
		const draftSet = getDraftForKey(keyIndex);
		const qs = inRangeQs();
		if (qs.length === 0) return false;
		for (let i = 0; i < qs.length; i++) {
			if (!draftSet.has(qs[i])) return false;
		}
		return true;
	};

	// Toggle behavior: if all are selected, clear; else select all
	const selectAllInRange = (keyIndex) => {
		const allSelected = areAllSelectedInRange(keyIndex);
		if (allSelected) {
			setDraftForKey(keyIndex, () => new Set());
			return;
		}
		const qs = inRangeQs();
		setDraftForKey(keyIndex, () => new Set(qs));
	};

	// Build the set of "unmodified" questions in range (no omit/mr/weight)
	const getUnmodifiedQs = (keyIndex) => {
		const { existing } = ensureKey(state, keyIndex);
		const items = existing.itemsByQuestion || {};
		const qs = inRangeQs();
		const out = [];
		for (let i = 0; i < qs.length; i++) {
			const q = qs[i];
			const attrs = items[q] || {};
			const hasWeight = attrs.weight != null && String(attrs.weight).trim().length > 0;
			const isOmitted = !!attrs.omit;
			const isMR = !!attrs.mr;
			if (!hasWeight && !isOmitted && !isMR) out.push(q);
		}
		return out;
	};

	// "Select All Blank" toggle: if current draft already equals the blank set, clear; else select blanks
	const selectAllBlankInRange = (keyIndex) => {
		const blankQs = getUnmodifiedQs(keyIndex);
		const current = getDraftForKey(keyIndex);
		// compare sets quickly
		let same = current.size === blankQs.length;
		if (same) {
			for (let i = 0; i < blankQs.length; i++) {
				if (!current.has(blankQs[i])) {
					same = false;
					break;
				}
			}
		}
		setDraftForKey(keyIndex, () => (same ? new Set() : new Set(blankQs)));
	};

	return (
		<div>
			<Button onClick={handleOpen}>
				<CustomTooltip
					title="Advanced Settings"
					description="Configuration for 'atypical' exams such as weighted questions, non-contiguous answer keys, multiple response, and formula scoring. Not required for the vast majoriy of grading requests."
				>
					<div className="text-sm text-gray-500">ADVANCED SETTINGS</div>
				</CustomTooltip>
			</Button>
			{/* <Button onClick={handleOpen}>Open modal</Button> */}
			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
			>
				<Box sx={style}>
					<div className="flex flex-col justify-between h-full">
						<div className="grid grid-cols-12 ">
							<div className="col-span-3  border-[1px] bg-white overflow-y-auto  rounded-l-md shadow-sm">
								<div className="flex flex-col h-full">
									<div className="h-1/3 border-2 flex  flex-col  gap-2  items-start p-2">
										<div className="text-center w-full">Select Advanced Settings</div>
										<Divider className="w-full" />
										<FormControlLabel
											className=" "
											labelPlacement="start"
											control={
												<Switch
													disabled={advancedSettings.compositeScoring}
													checked={advancedSettings.formulaScoring}
													onChange={(e) =>
														setAdvancedSettings((prev) => ({
															...prev,
															formulaScoring: e.target.checked,
														}))
													}
												/>
											}
											label={
												<CustomTooltip
													title="Formula Scoring"
													description="Scores may be computed for as many as 4 keys with formula scoring. The formula used is (R - W)/d where 
            R is the sum of correct responses, 
            W is the sum of incorrect responses (omits are not counted), and
            d is the number of response alternatives minus 1"
												>
													<div className="text-sm text-primary underline">Formula Scoring</div>
												</CustomTooltip>
											}
										/>
										<Divider className="w-full" />
										<FormControlLabel
											labelPlacement="start"
											className="  "
											control={
												<Switch
													disabled={advancedSettings.formulaScoring}
													checked={advancedSettings.compositeScoring}
													onChange={(e) => {
														setAdvancedSettings((prev) => ({
															...prev,
															compositeScoring: e.target.checked,
														}));
													}}
												/>
											}
											label={
												<CustomTooltip
													title="Composite Scoring"
													description="Apply different weights to questions on your exam."
													example="Questions 1-10 are worth 2 marks each, Questions 11-50 are worth 1 mark each."
												>
													<div className="text-sm text-primary underline">
														Weighted/Composite Scoring
													</div>
												</CustomTooltip>
											}
										/>
										<Divider className="w-full" />
										<FormControlLabel
											labelPlacement="start"
											className=" "
											control={
												<Switch
													//disabled={advancedSettings.formulaScoring}
													checked={advancedSettings.mrscor}
													onChange={(e) => {
														setAdvancedSettings((prev) => ({
															...prev,
															mrscor: e.target.checked,
														}));
														setState((prev) => ({
															...prev,
															program: e.target.checked ? "2" : "1",

															d: e.target.checked ? "1" : "2",
															e: e.target.checked ? "1" : state.e,
															m: e.target.checked ? "4" : "0",
														}));
														if (Number(state.nKeys) > 1 && e.target.checked) {
															setState((prev) => ({
																...prev,
																nKeys: "1",
															}));
														}
													}}
												/>
											}
											label={
												<CustomTooltip
													title="Multiple Response Exam"
													description="An exam where each question may have more than one correct answer."
												>
													<div className="text-sm text-primary underline">
														Multiple Response Exam
													</div>
												</CustomTooltip>
											}
										/>
										{/* <Divider className="w-full" /> */}
									</div>
									<div className="h-full border-2 flex flex-col gap-2 items-start p-2">
										<div className="text-center w-full">Checklist</div>
										<Divider className="w-full" />
										{state.nKeys > 1 && (
											<div className="w-full">
												<div className="font-medium mb-1 text-primary ">
													<CustomTooltip
														title="Version Codes"
														description="The version codes entered for each answer key. These codes must match what is submitted for scoring, as they are the only way we can accurately apply this advanced configuration to your exam."
													>
														Version Codes
													</CustomTooltip>
												</div>
												<Divider className="w-full" />
												<div className={`text-sm text-${versionSummary[1]}-500 mt-2`}>
													{versionSummary[1] === "yellow" && "⚠️"}
													{versionSummary[0]}
												</div>
											</div>
										)}
										{advancedSettings.compositeScoring && <Divider className="w-full" />}

										{advancedSettings.compositeScoring && (
											<div className="w-full">
												<div className="font-medium mb-1 text-primary underline">
													<CustomTooltip
														title="Weight Assignments"
														description="A count of how many questions have had weights applied, per answer key. Any questions that are left unweighted will be given a weight of 1 on submission."
													>
														Weight Assignments
													</CustomTooltip>
												</div>
												<Divider className="w-full" />

												{keySummaries.length === 0 ? (
													<div className="text-sm text-gray-500 mt-2">No keys configured yet.</div>
												) : (
													<div className="mt-2 space-y-1 w-full">
														{keySummaries.map(
															({ idx, label, total, weighted, mrCount, totalMarks }) => (
																<div
																	key={idx}
																	className="flex items-center justify-between text-sm text-gray-700"
																>
																	<span className="truncate">Key {label}</span>
																	<span className="tabular-nums">
																		{weighted} / {total} weighted ({totalMarks} total)
																	</span>
																</div>
															),
														)}
													</div>
												)}
											</div>
										)}

										{advancedSettings.mrscor && <Divider className="w-full" />}
										{advancedSettings.mrscor && (
											<div className="w-full">
												<div className="font-medium mb-1 text-primary underline">
													<CustomTooltip
														title="Multiple Response Config"
														description="A count of how many questions have been marked as multiple response, per answer key. Questions not marked as MR will be treated as single-response."
													>
														Multiple Response Config
													</CustomTooltip>
												</div>
												<Divider className="w-full" />
												{keySummaries.length === 0 ? (
													<div className="text-sm text-gray-500 mt-2">No keys configured yet.</div>
												) : (
													<div className="mt-2 space-y-1 w-full">
														{keySummaries.map(({ idx, label, total, weighted, mrCount }) => (
															<div
																key={idx}
																className="flex items-center justify-between text-sm text-gray-700"
															>
																<span className="truncate">Key {label}</span>
																<span className="tabular-nums">
																	{mrCount} / {total} marked MR
																</span>
															</div>
														))}
													</div>
												)}
											</div>
										)}
									</div>
								</div>
							</div>
							<div className="h-[80dvh] flex flex-col col-span-9 overflow-y-scroll">
								<div className="p-4">
									<AdvancedQuestionInputs handleMCExamChange={handleMCExamChange} />

									{advancedSettings.mrscor && (
										<MRScor
											navigate={navigate}
											token={token}
											ccid={ccid}
											classNumList={classNumList}
											handlePrint={handlePrint}
											instructorClasses={instructorClasses}
											confirmedOfficeInstructor={confirmedOfficeInstructor}
											handleDoneOfficeInstructor={handleDoneOfficeInstructor}
											ccidString={ccidString}
										/>
									)}
									<Divider className="w-full" />

									<div className="text-primary font-medium py-4 flex items-center">
										Configure Answer Key(s):
										<CustomTooltip
											title="Answer Key Configuration"
											description="Set up one or more answer keys for your exam. You can configure per-question weights, omit questions from scoring, and mark questions as multiple response (MR). You can select entire rows with the checkbox on the left, or click and drag to select multiple questions. After selecting questions, choose a tool (Set Weight, Toggle Omit, or Toggle MR) and click 'Apply to Selection' to apply the tool to the selected questions."
										>
											<HelpIcon fontSize="small" className="ml-1 mb-1 text-gray-500" />
										</CustomTooltip>
									</div>
									<TabContext value={tabValue}>
										<div className="shadow-sm rounded-t-md border-b bg-white">
											<TabList onChange={handleChangeTabs} aria-label="Answer key tabs">
												{Array.from({ length: Number(state.nKeys) }).map((_, index) => (
													<Tab
														key={index}
														label={`Key ${
															keyDataAt(state, index).version
																? keyDataAt(state, index).version
																: index + 1
														}`}
														value={`${index + 1}`}
													/>
												))}
											</TabList>
										</div>

										{Array.from({ length: Number(state.nKeys) }).map((_, index) => (
											<div key={index + 1}>
												<TabPanel
													value={String(index + 1)}
													key={index + 1}
													className=" shadow-md bg-white"
												>
													<div className="flex flex-col gap-2">
														{Number(state.nKeys) > 1 && (
															<div className="flex items-center font-medium text-black    ">
																{keyDataAt(state, index).version ? (
																	`Version: ${keyDataAt(state, index).version}`
																) : (
																	<div>Enter Version Code (REQUIRED) :</div>
																)}
																{Number(state.nKeys) > 1 && (
																	<div className="flex items-center">
																		<input
																			type="text"
																			maxLength={6}
																			value={keyDataAt(state, index)?.version || ""}
																			onChange={(e) => {
																				setState((prev) => {
																					const { versionKey, keysCopy, existing } = ensureKey(
																						prev,
																						index,
																					);
																					keysCopy[versionKey] = {
																						...existing,
																						version: e.target.value,
																					};
																					return { ...prev, keys: keysCopy };
																				});
																			}}
																			className={`ml-2 p-1 border rounded w-20 text-center ${
																				keyDataAt(state, index).version?.length > 0
																					? "border-gray-300"
																					: "ring-2 ring-primaryGold animate-pulse "
																			}`}
																		/>
																		<CustomTooltip
																			title="Version Code"
																			description="The special code/version code that will appear on each answer key. This MUST match what is submitted for scoring, as it is the only way we can apply the configuration specified below to your exam."
																		>
																			<InfoOutlineIcon
																				fontSize="small"
																				className=" text-gray-500 ml-2"
																			/>
																		</CustomTooltip>
																	</div>
																)}
															</div>
														)}
														<Divider className="w-full" />

														<div>
															<div className="flex flex-col  gap-2 ">
																<div className="flex flex-wrap items-center gap-2 max-w-[30rem] justify-between">
																	<div className="font-medium w-[120px] ">Selected Tool:</div>
																	<div className="flex flex-wrap gap-2  w-[calc(100%-130px)] justify-between">
																		{advancedSettings.compositeScoring && (
																			<button
																				className={`px-3 py-1 rounded border ${
																					activeTool === "weight"
																						? "bg-primary text-white"
																						: "bg-white"
																				}`}
																				onClick={() => {
																					if (activeTool === "weight") {
																						setActiveTool("");
																						setActiveWeight(null);
																					} else setActiveTool("weight");
																				}}
																			>
																				Set Weight
																			</button>
																		)}

																		<button
																			className={`px-3 py-1 rounded border ${
																				activeTool === "omit" ? "bg-primary text-white" : "bg-white"
																			}`}
																			onClick={() => {
																				if (activeTool === "omit") {
																					setActiveTool("");
																				} else setActiveTool("omit");
																			}}
																		>
																			Toggle Omit
																		</button>
																		{advancedSettings.mrscor && (
																			<button
																				className={`px-3 py-1 rounded border ${
																					activeTool === "mr" ? "bg-primary text-white" : "bg-white"
																				}`}
																				onClick={() => {
																					if (activeTool === "mr") {
																						setActiveTool("");
																					} else {
																						setActiveTool("mr");
																					}
																				}}
																			>
																				Toggle MR
																			</button>
																		)}
																	</div>
																</div>
																{activeTool === "weight" && advancedSettings.compositeScoring && (
																	<div className="flex flex-wrap items-center gap-2  max-w-[30rem]  justify-between">
																		<div className="font-medium w-[120px] ">Weight:</div>
																		<div className="gap-2 flex flex-wrap  w-[calc(100%-130px)] justify-between">
																			{listOfWeights.map((option) => (
																				<button
																					key={option.label}
																					className={`px-2 py-1 rounded border ${
																						!activeWeight && "ring-2 animate-pulse ring-yellow-500"
																					} ${
																						String(activeWeight) === String(option.value)
																							? "bg-primary text-white"
																							: "bg-white"
																					}`}
																					onClick={() => setActiveWeight(option.value)}
																				>
																					{option.label}
																				</button>
																			))}
																		</div>
																	</div>
																)}
															</div>
														</div>
														<Divider className="w-full" />
														<div className="bg-white shadow-sm ">
															{activeTool !== "" ? (
																<div className="bg-secondaryGreen/20 px-1 rounded-md flex items-center justify-center gap-1">
																	Applying
																	<span className="font-medium">
																		{activeTool === "mr" ? "Multiple Response" : activeTool}
																	</span>
																	{activeTool === "weight" && (
																		<div className="flex items-center gap-1">
																			of
																			<span className="font-medium ">{activeWeight}</span>
																		</div>
																	)}
																	to
																	<span className="font-medium">
																		{Array.from(getDraftForKey(index)).length}
																	</span>
																	questions
																</div>
															) : Array.from(getDraftForKey(index)).length > 0 ? (
																<div className="bg-secondaryOrange/20 px-1 rounded-md flex items-center justify-center">
																	<InfoOutlineIcon
																		fontSize="small"
																		className="text-gray-600 mr-2"
																	/>
																	{Array.from(getDraftForKey(index)).length} items selected. Please
																	select the applicable tool and click "Apply" to add a flag to each
																	item.
																</div>
															) : (
																<div className="bg-secondaryOrange/20 px-1 rounded-md flex items-center justify-center">
																	Please select questions below to begin applying configurations.
																</div>
															)}
														</div>
														<Divider className="w-full" />
														{state.hqNum && Number(state.hqNum) - Number(state.lqNum) + 1 > 0 ? (
															<>
																<div className="flex gap-2 my-2">
																	<button
																		onClick={() => selectAllInRange(index)}
																		className="px-3 py-2 bg-blue-500 text-white rounded"
																	>
																		Select All
																	</button>
																	<button
																		onClick={() => selectAllBlankInRange(index)}
																		className="px-3 py-2 border-blue-500 border-2 text-blue-500 rounded"
																	>
																		Select Blank
																	</button>

																	<button
																		disabled={
																			activeTool === "" ||
																			(activeTool === "weight" && !activeWeight) ||
																			Array.from(getDraftForKey(index)).length === 0
																		}
																		onClick={() => commitDraftForKey(index)}
																		className={`ml-auto px-3 py-2 disabled:bg-gray-400 disabled:cursor-not-allowed bg-secondaryGreen text-white rounded`}
																	>
																		{activeTool === "weight" && !activeWeight ? (
																			<CustomTooltip title="Select Weight Value to Apply">
																				Apply
																			</CustomTooltip>
																		) : (
																			"Apply"
																		)}
																	</button>
																</div>

																{Array.from({ length: numRows }).map((_, rowIndex) => {
																	const rowQuestions = [];
																	for (let i = 0; i < columns; i++) {
																		const qNum = Number(state.lqNum) + rowIndex * columns + i;
																		if (qNum <= Number(state.hqNum)) rowQuestions.push(qNum);
																	}

																	const keyData = ensureKey(state, index).existing;
																	const items = keyData.itemsByQuestion || {};

																	return (
																		<div key={rowIndex} className="flex items-center gap-2 my-1">
																			<button
																				className={`${
																					rowQuestions.every((q) => getDraftForKey(index).has(q))
																						? "bg-indigo-500 text-white"
																						: "bg-white"
																				} h-4 w-4 flex-shrink-0 rounded border border-gray-300 text-sm
																						`}
																				onClick={() => toggleSelectRow(rowIndex, index)}
																			></button>
																			<div className="grid grid-cols-10 gap-2 flex-grow">
																				{rowQuestions.map((q) => {
																					const attrs = items[q] || {};
																					// const isSelected = getDraftForKey(index).has(q);

																					// Visual states
																					const committedWeight = attrs.weight
																						? String(attrs.weight)
																						: null;
																					const isOmitted = !!attrs.omit;
																					const isMR = !!attrs.mr;

																					return (
																						<button
																							key={q}
																							onMouseDown={(e) => {
																								e.preventDefault(); // stop text selection
																								beginDrag(q, index);
																							}}
																							onMouseEnter={() => hoverDrag(q)}
																							onTouchStart={(e) => {
																								// simple touch support: treat like mousedown
																								beginDrag(q, index);
																							}}
																							onTouchMove={(e) => {
																								// hit testing for touch move: find the element under the finger
																								const t = e.touches[0];
																								const el = document.elementFromPoint(
																									t.clientX,
																									t.clientY,
																								);
																								// If you add a data-q attribute on the buttons, you can read it here
																								const qAttr = el?.getAttribute?.("data-q");
																								if (qAttr) hoverDrag(Number(qAttr));
																							}}
																							data-q={q}
																							className={[
																								"relative p-2 border rounded transition text-sm",
																								getDraftForKey(index).has(q)
																									? "ring-2 ring-indigo-500 bg-white"
																									: "bg-white hover:bg-gray-50",
																								isOmitted ? "opacity-60" : "",
																								"select-none", // tailwind: prevent text highlight while dragging
																							].join(" ")}
																							title={`Q${q} ${
																								committedWeight ? ` • W:${committedWeight}` : ""
																							}${isOmitted ? " • OMIT" : ""}${isMR ? " • MR" : ""}`}
																						>
																							{q}

																							{/* Weight badge (top-right) */}
																							{committedWeight && !isOmitted && (
																								<span className="absolute -top-1 -right-1 text-[10px] leading-none px-1 rounded-full border bg-blue-600 text-white border-blue-700">
																									{committedWeight}
																								</span>
																							)}

																							{/* OMIT badge (bottom-left) */}
																							{isOmitted && (
																								<span className="absolute -bottom-1 -left-1 text-[10px] leading-none px-1 rounded-full border bg-gray-300 text-gray-800 border-gray-400">
																									OMIT
																								</span>
																							)}

																							{/* MR badge (bottom-right) */}
																							{isMR && !isOmitted && (
																								<span className="absolute -bottom-1 -right-1 text-[10px] leading-none px-1 rounded-full border bg-amber-400 text-black border-amber-500">
																									MR
																								</span>
																							)}
																						</button>
																					);
																				})}
																			</div>
																		</div>
																	);
																})}
																<div className="flex justify-between my-2">
																	<button
																		onClick={() => clearDraft(index)}
																		className="px-3 py-2 bg-gray-300 rounded"
																	>
																		Clear Selection
																	</button>
																	<button
																		onClick={() => resetKeyItems(index)}
																		className="px-3 py-2 text-red-700 hover:underline rounded"
																	>
																		Reset Applied
																	</button>
																</div>
															</>
														) : (
															<p className="text-gray-500 mt-2">
																Please specify the question range above.
															</p>
														)}
													</div>
												</TabPanel>
											</div>
										))}
									</TabContext>
								</div>
							</div>
						</div>

						<Button onClick={handleClose} variant="contained" disabled={!canSubmit}>
							{!canSubmit
								? "Version Codes Required for Weighted/MRScor Exams With Multiple Keys"
								: "Save & Close"}
						</Button>
					</div>
				</Box>
			</Modal>
		</div>
	);
}
